import { EventTypeFilterGroup } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { filtersAtom } from '@/atoms/filters/filter';
import { filterAtomDefaults } from '@/consts/defaults/filterAtomDefaults';
import { ConfigFilterAtomValues } from '@/types/userConfigs';
import { isNullish } from '@/utils/predicates';
import { trackFilterChange } from '../tracking';

type FilterKey = keyof ConfigFilterAtomValues;

// Identifies the group type — not user-selected filter values, never tracked
const GROUP_METADATA_KEYS = new Set<string>(['parentEventType', 'eventTypeOperator']);

// filterAtomDefaults is typed ConfigFilterAtomValues and FilterKey = keyof ConfigFilterAtomValues,
// so key indexes directly without a cast.
const isDefault = (key: FilterKey, value: unknown): boolean =>
  JSON.stringify(value) === JSON.stringify(filterAtomDefaults[key]);

// CustomShape items (pitch zones) have an id and coordinate points — track only the id
const isCustomShape = (value: unknown): value is { id: string | number; points: string } =>
  typeof value === 'object' && value !== null && 'id' in value && 'points' in value;

const serialiseItem = (value: unknown): string => {
  if (isCustomShape(value)) return String(value.id);
  if (typeof value === 'string') return value;
  // objects with .value (e.g. NumberOption for deselectedGameIds) — extract the value
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    if (!isNullish(obj.value)) return String(obj.value);
  }
  return JSON.stringify(value);
};

// Extract a readable selection string from any filter value:
// objects with .key (GameRangeFilterOption), objects with .value (TranslatedOption), or plain scalars
const extractSelection = (value: unknown): string => {
  if (typeof value === 'object' && value !== null) {
    const valueAsRecord = value as Record<string, unknown>;
    if (typeof valueAsRecord.key === 'string') return valueAsRecord.key;
    if (!isNullish(valueAsRecord.value)) return String(valueAsRecord.value);
  }
  return String(value ?? '');
};

const diffArrayValues = (prevArr: unknown[], nextArr: unknown[], filterType: string, filterSource: string) => {
  // Serialise each array once and reuse for both Set lookup and iteration
  const prevSerialised = prevArr.map(serialiseItem);
  const nextSerialised = nextArr.map(serialiseItem);
  const prevSet = new Set(prevSerialised);
  const nextSet = new Set(nextSerialised);
  nextSerialised
    .filter(serialisedValue => !prevSet.has(serialisedValue))
    .forEach(serialisedValue => trackFilterChange(filterType, 'Applied', filterSource, serialisedValue));
  prevSerialised
    .filter(serialisedValue => !nextSet.has(serialisedValue))
    .forEach(serialisedValue => trackFilterChange(filterType, 'Removed', filterSource, serialisedValue));
};

// Diffs filter properties inside each group rather than treating the whole group as one value.
// Groups are matched by parentEventType so modifying a group's filters produces granular events
// (e.g. filterType:"passType", filterSelection:"cross") rather than one opaque group-level event.
const diffEventFilterGroups = (
  prevGroups: EventTypeFilterGroup[],
  nextGroups: EventTypeFilterGroup[],
  filterSource: string,
) => {
  const prevMap = new Map(prevGroups.map(group => [group.parentEventType, group]));
  const nextMap = new Map(nextGroups.map(group => [group.parentEventType, group]));

  nextMap.forEach((nextGroup, parentEventType) => {
    const prevGroup = prevMap.get(parentEventType);
    // EventTypeFilterGroup uses a strict Zod schema — runtime keys match the type exactly,
    // so the cast from string[] is safe here.
    (Object.keys(nextGroup) as (keyof EventTypeFilterGroup)[]).forEach(prop => {
      if (GROUP_METADATA_KEYS.has(prop)) return;
      const previousValue = prevGroup?.[prop];
      const nextValue = nextGroup[prop];
      if (JSON.stringify(previousValue) === JSON.stringify(nextValue)) return;
      if (Array.isArray(nextValue)) {
        diffArrayValues(
          // previousValue is ConfigFilterAtomValues[key] — a wide union; cast to unknown[] after Array.isArray guard
          Array.isArray(previousValue) ? (previousValue as unknown[]) : [],
          nextValue as unknown[],
          String(prop),
          filterSource,
        );
      } else if (!isNullish(nextValue)) {
        trackFilterChange(String(prop), 'Applied', filterSource, extractSelection(nextValue));
      } else if (!isNullish(previousValue)) {
        trackFilterChange(String(prop), 'Removed', filterSource, extractSelection(previousValue));
      }
    });
  });

  // Fire Removed for every filter value in groups that were deleted entirely
  prevMap.forEach((prevGroup, parentEventType) => {
    if (nextMap.has(parentEventType)) return;
    // Same strict-schema cast rationale as above
    (Object.keys(prevGroup) as (keyof EventTypeFilterGroup)[]).forEach(prop => {
      if (GROUP_METADATA_KEYS.has(prop)) return;
      const groupPropValue = prevGroup[prop];
      if (Array.isArray(groupPropValue)) {
        // groupPropValue is EventTypeFilterGroup[keyof EventTypeFilterGroup]; cast to unknown[] after Array.isArray guard
        (groupPropValue as unknown[]).forEach(item =>
          trackFilterChange(String(prop), 'Removed', filterSource, serialiseItem(item)),
        );
      } else if (!isNullish(groupPropValue)) {
        trackFilterChange(String(prop), 'Removed', filterSource, extractSelection(groupPropValue));
      }
    });
  });
};

export const useTrackFilterChanges = (filterKeys: readonly FilterKey[], filterSource: string) => {
  const filters = useAtomValue(filtersAtom);
  const prevRef = useRef(filters);

  useEffect(() => {
    const prev = prevRef.current;
    filterKeys.forEach(key => {
      const previousValue = prev[key];
      const nextValue = filters[key];
      if (JSON.stringify(previousValue) === JSON.stringify(nextValue)) return;

      if (key === 'eventFilterGroups') {
        diffEventFilterGroups(
          // TS can't narrow prev[key] to EventTypeFilterGroup[] inside a forEach key === check (indexed-access narrowing limitation);
          // ?? [] guards against undefined reaching diffEventFilterGroups at runtime even though the type is non-nullable
          (previousValue ?? []) as EventTypeFilterGroup[],
          (nextValue ?? []) as EventTypeFilterGroup[],
          filterSource,
        );
      } else if (Array.isArray(previousValue) || Array.isArray(nextValue)) {
        // handles null → array and array → null transitions (e.g. competitionIds defaults to null)
        diffArrayValues(
          // ConfigFilterAtomValues[key] is a wide union; cast to unknown[] after Array.isArray guard
          Array.isArray(previousValue) ? (previousValue as unknown[]) : [],
          Array.isArray(nextValue) ? (nextValue as unknown[]) : [],
          String(key),
          filterSource,
        );
      } else {
        const appliedVsRemoved = isDefault(key, nextValue) ? 'Removed' : 'Applied';
        const selectionValue = appliedVsRemoved === 'Applied' ? nextValue : previousValue;
        trackFilterChange(String(key), appliedVsRemoved, filterSource, extractSelection(selectionValue));
      }
    });
    prevRef.current = filters;
  }, [filters, filterKeys, filterSource]);
};
